import type { HubSpotProperty } from '@/store/features/crm.feature';
import type { PlayCrm } from '@/types/custom-playbooks';

export type ProjectionCrm = PlayCrm;

const ATTIO_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** An Attio id (a list, a status, a record) as Attio's routes give it. */
export function isAttioId(value: unknown): boolean {
  return typeof value === 'string' && ATTIO_ID.test(value);
}

/** Each CRM's field name grammar, as the API checks it
 *  (`utils/play_crm_fields.py`). Attio names attributes by slug. */
const FIELD_NAME: Record<ProjectionCrm, RegExp> = {
  salesforce: /^[A-Za-z][A-Za-z0-9_]{0,79}$/,
  hubspot: /^[a-z0-9_]{1,100}$/,
  attio: /^[a-z][a-z0-9_]{0,99}$/,
};

/** A Contact's parent fields: Salesforce reads them on the Account, HubSpot
 *  and Attio on the company. */
const PARENT: Record<ProjectionCrm, { prefix: string; group: string }> = {
  salesforce: { prefix: 'Account', group: 'Account' },
  hubspot: { prefix: 'company', group: 'Company' },
  attio: { prefix: 'company', group: 'Company' },
};

/** Whether `name` is a field name `crm` accepts, without a parent prefix. */
export function isCrmFieldName(name: string, crm: ProjectionCrm) {
  return FIELD_NAME[crm].test(name);
}

/** Match the accepted read projection grammar, including Contact parents only. */
export function isCrmProjectionField(
  name: string,
  crm: ProjectionCrm,
  objectType: string
) {
  const prefix = `${PARENT[crm].prefix}.`;
  const parent = name.startsWith(prefix);
  if (parent && objectType !== 'Contact') return false;
  return isCrmFieldName(parent ? name.slice(prefix.length) : name, crm);
}

export function crmProjectionProperties(
  crm: ProjectionCrm,
  objectType: string,
  own: HubSpotProperty[],
  parent: HubSpotProperty[]
): HubSpotProperty[] {
  const ownProperties = own.filter(
    (row) =>
      row &&
      typeof row.name === 'string' &&
      !row.hidden &&
      isCrmProjectionField(row.name, crm, objectType)
  );
  if (objectType !== 'Contact') return ownProperties;
  const { prefix, group } = PARENT[crm];
  return [
    ...ownProperties,
    ...parent
      .filter(
        (row) =>
          row &&
          typeof row.name === 'string' &&
          !row.hidden &&
          isCrmProjectionField(row.name, crm, 'Account')
      )
      .map((row) => ({
        ...row,
        name: `${prefix}.${row.name}`,
        label: `${group} › ${row.label}`,
        group_name: group,
      })),
  ];
}
